import React, { useState } from 'react'
import SbEditable from 'storyblok-react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Sentry from '@sentry/react'
import InputMask from 'react-input-mask'
import * as Yup from 'yup'

import Components from '../components.js'
import { leads, metrics } from '../../services'
import TrackFormDirty from '../TrackFormDirty'
import { PhoneRegex } from '../../constants'
import './lead-form-con-ed.scss'

// I have a decent idea what this is doing. Using https://github.com/formium/formik/blob/master/examples/MultistepWizard.js

const defaultFormValues = {
  name: '',
  address: '',
  city: 'NY',
  zip: '',
  email: '',
  phone: '',
  propertyAttributes: '',
  isUtilityCustomer: false,
  existingSolar: false,
  isHomeowner: false,
}

const Wizard = ({ children, initialValues, onStep1Done, onSubmit, nextButtonText, submitButtonText, submittingButtonText, buttonStyle, submitState, errorMessage }) => {
  const [stepNumber, setStepNumber] = useState(0)
  const [isDirty, setIsDirty] = useState(false);
  const steps = React.Children.toArray(children)
  const [snapshot, setSnapshot] = useState(initialValues)

  const step = steps[stepNumber]
  const totalSteps = steps.length
  const isLastStep = stepNumber === totalSteps - 1

  const next = values => {
    const nextStepNumber = Math.min(stepNumber + 1, totalSteps - 1)
    metrics.trackLeadFormStep(nextStepNumber, 'LeadFormConEd', '')

    setSnapshot(values)
    setStepNumber(nextStepNumber)
  }

  const handleSubmit = async (values, bag) => {
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, bag)
    }

    if (isLastStep) {
      return onSubmit(values, bag)
    } else {
      if (onStep1Done) {
        if (!onStep1Done(values)) {
          return;
        }
      }

      bag.setTouched({})
      next(values)
    }
  }

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={step.props.validationSchema}
    >
      {formik => {
        const buttonText = (formik.isSubmitting ? submittingButtonText : (isLastStep ? submitButtonText : nextButtonText));

        return (
          <>
            <TrackFormDirty formComponent='LeadFormConEd' isDirty={isDirty} setIsDirty={setIsDirty} />
            <Form className="input-form">
              <div className="fields">
                {step}
              </div>
              {
                (submitState === 'failed' || !!errorMessage) &&
                <div className='error'>{errorMessage}</div>
              }
              <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">{buttonText}</button>
            </Form>
          </>
        );
      }}
    </Formik>
  )
}

const WizardStep = ({ children }) => children;

const LeadFormConEd = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');

  const onEligibilityCheck = (values) => {
    let isEligibleZip = true;

    if (props.blok.eligible_zips) {
      const zipList = JSON.parse(props.blok.eligible_zips);
      isEligibleZip = zipList.includes(values.zip);
    }

    if (!isEligibleZip) {
      setSubmitState('ineligible');
      return false;
    }

    if (!values.email && !values.phone) {
      setErrorMessage('Please provide either a valid e-mail address or valid phone number (or both)');
      return false;
    } else {
      setErrorMessage('');
    }

    return true;
  }

  const onSubmit = async (values) => {
    setSubmitState('');

    if (!values.email && !values.phone) {
      setSubmitState('failed');
      setErrorMessage('Please provide either a valid e-mail address or valid phone number (or both)');
      return;
    }

    try {
      let utmContent = props.blok.utm_content || '';
      const isProd = window.location.origin.indexOf("swellenergy.com") > -1 && window.location.origin.indexOf("sbx") === -1;
      const leadSourceAccounts = props.blok.lead_source_account ? JSON.parse(props.blok.lead_source_account) : { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };

      await leads.submitLead({
        ...values,
        utmContent,
        leadSourceMedium: props.blok.lead_source_medium || 'Swell Energy',
        leadSource: props.blok.lead_source || 'Online Media',
        leadSourceAccount: isProd ? leadSourceAccounts.prod : leadSourceAccounts.dev,
        notes: `Suitable location for a battery 10 feet from windows/doors/fence lines (no roofs)? ${values.hasSuitableLocation ? 'Yes' : 'No'}\r\n` +
          `My property is: ${values.propertyAttributes}`
      });

      metrics.trackLeadFormSubmit(window.location.origin + window.location.pathname, 'LeadFormConEd', utmContent);
      setSubmitState('success');
    } catch (e) {
      Sentry.captureException(e, {
        lead: values
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfce'];
  containerClasses.push('theme-' + props.blok.theme);

  const nextButtonText = props.blok.next_button_text || 'Get Started Today';
  const submitButtonText = props.blok.submit_button_text || 'Submit For More Information';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {
          submitState !== 'success' &&
          submitState !== 'ineligible' &&
          <Wizard
            initialValues={defaultFormValues}
            onStep1Done={(v) => onEligibilityCheck(v)}
            onSubmit={onSubmit}
            nextButtonText={nextButtonText}
            submitButtonText={submitButtonText}
            submittingButtonText={submittingButtonText}
            buttonStyle={buttonStyle}
            submitState={submitState}
            errorMessage={errorMessage}
          >
            <WizardStep
              validationSchema={
                Yup.object().shape({
                  name: Yup.string().required('Please enter a valid first name and last name.'),
                  email: Yup.string().email('Please enter a valid e-mail address'),
                  phone: Yup.string().matches(PhoneRegex, 'Please enter a valid phone number.'),
                  zip: Yup.string().required('Please enter a valid ZIP Code.').matches(/\d{5}/, 'Please enter a valid ZIP Code.')
                })
              }
            >
              {props.blok.step_1_preform_content && props.blok.step_1_preform_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
              <div className="field">
                <Field name="name" placeholder="Name" />
                <ErrorMessage className="error" component="div" name="name" />
              </div>
              <div className="field">
                <Field name="email" placeholder="Email" />
                <ErrorMessage className="error" component="div" type="email" name="email" />
              </div>
              <div className="field">
                <Field name="phone" placeholder="Phone">
                  {({ field }) => (
                    <InputMask {...field} mask="(999) 999-9999" placeholder="Phone" type="text" alwaysShowMask={false} />
                  )}
                </Field>
                <ErrorMessage className="error" component="div" name="phone" />
              </div>
              <div className="field">
                <Field name="zip" placeholder="ZIP Code">
                  {({ field }) => (
                    <InputMask {...field} mask="99999" placeholder="ZIP Code" type="text" alwaysShowMask={false} />
                  )}
                </Field>
                <ErrorMessage className="error" component="div" name="zip" />
              </div>
            </WizardStep>
            <WizardStep
              validationSchema={
                Yup.object().shape({
                  address: Yup.string().required('Please enter your street address'),
                  propertyAttributes: Yup.string().required('Please select a value'),
                })
              }
            >
              {props.blok.step_2_preform_content && props.blok.step_2_preform_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
              <div className="field">
                <Field name="address" placeholder="Street Address" />
                <ErrorMessage className="error" component="div" name="address" />
              </div>
              <div className="field">
                <Field name="propertyAttributes" as="select" placeholder="My property is...">
                  <option value="" selected disabled>My property is...</option>
                  <option value='Fully attached 2+ sides'>Fully attached 2+ sides</option>
                  <option value='Semi-attached 1 side'>Semi-attached 1 side</option>
                  <option value='Fully detached'>Fully detached</option>
                </Field>
                <ErrorMessage className="error" component="div" name="propertyAttributes" />
              </div>
              <div className="field-checkbox">
                <Field name="isHomeowner" type="checkbox" id="isHomeowner" />
                <label htmlFor="isHomeowner">I am the property owner</label>
              </div>
              <div className="field-checkbox">
                <Field name="hasSuitableLocation" type="checkbox" id="hasSuitableLocation" />
                <label htmlFor="hasSuitableLocation">I have a suitable location for a battery 10 feet from windows/doors/fence lines (no roofs)</label>
              </div>
              <div className="field-checkbox">
                <Field name="existingSolar" type="checkbox" id="existingSolar" />
                <label htmlFor="existingSolar">I have existing solar (not required)</label>
              </div>
              <div className="field-checkbox">
                <Field name="isUtilityCustomer" type="checkbox" id="isUtilityCustomer" />
                <label htmlFor="isUtilityCustomer">I am a Con Edison Electric Customer</label>
              </div>
              <span className='disclaimer'>
                    By submitting this form, I agree to the{' '}
                      <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                      on behalf of Swell Energy, including by email, calls, and text messages to any
                      telephone number that I provide, about my interest in Swell Energy products
                      and services. I understand my consent is not required to purchase.
                    </span>
            </WizardStep>
          </Wizard>
          // <Formik
          //   initialValues={modeDefaults}
          //   onSubmit={onSubmit}
          //   validationSchema={Yup.object().shape(modeValidation)}
          // >
          //   {
          //     formik => {
          //       const buttonText = (formik.isSubmitting ? submittingButtonText : submitButtonText);

          //       return (
          //         <Form className="input-form">
          //           <TrackFormDirty formComponent='LeadFormConEd' isDirty={isDirty} setIsDirty={setIsDirty} />
          //           <div className='fields'>
          //             {title && <h4 className='title'>{title}</h4>}
          //             <div className="field-checkbox">
          //               <Field name="isUtilityCustomer" type="checkbox" id="isUtilityCustomer" />
          //               <label htmlFor="isUtilityCustomer">I am a Con Edison customer</label>
          //             </div>
          //             <div className="field-checkbox">
          //               <Field name="isHomeowner" type="checkbox" id="isHomeowner" />
          //               <label htmlFor="isHomeowner">I own my home</label>
          //             </div>
          //             <div className="field-checkbox">
          //               <Field name="existingSolar" type="checkbox" id="existingSolar" />
          //               <label htmlFor="existingSolar">I have solar panels</label>
          //             </div>
          //             <div className="field-checkbox">
          //               <Field name="isInterestedInBattery" type="checkbox" id="isInterestedInBattery" />
          //               <label htmlFor="isInterestedInBattery">I am interested in a home battery</label>
          //             </div>
          //             <div className="field-checkbox">
          //               <Field name="isInterestedInSolarBattery" type="checkbox" id="isInterestedInSolarBattery" />
          //               <label htmlFor="isInterestedInSolarBattery">I am interested in getting solar panels and a home battery</label>
          //             </div>
          //             <div className="field-checkbox">
          //               <Field name="hasReceivedUtilityEmail" type="checkbox" id="hasReceivedUtilityEmail" />
          //               <label htmlFor="hasReceivedUtilityEmail">I received an email from Con Edison</label>
          //             </div>
          //             <div className="field">
          //               <Field name="name" placeholder="Name" />
          //               <ErrorMessage className="error" component="div" name="name" />
          //             </div>
          //             <div className="field">
          //               <Field name="address" placeholder="Address" />
          //               <ErrorMessage className="error" component="div" name="address" />
          //             </div>
          //             <div className="field">
          //               <Field name="city" placeholder="City" disabled />
          //               <ErrorMessage className="error" component="div" name="city" />
          //             </div>
          //             <div className="field">
          //               <Field name="zip" placeholder="ZIP Code">
          //                 {({ field }) => (
          //                   <InputMask {...field} mask="99999" placeholder="ZIP Code" type="text" alwaysShowMask={false} />
          //                 )}
          //               </Field>
          //               <ErrorMessage className="error" component="div" name="zip" />
          //             </div>
          //             <div className="field">
          //               <Field name="email" placeholder="Email" />
          //               <ErrorMessage className="error" component="div" type="email" name="email" />
          //             </div>
          //             <div className="field">
          //               <Field name="phone" placeholder="Phone">
          //                 {({ field }) => (
          //                   <InputMask {...field} mask="(999) 999-9999" placeholder="Phone" type="text" alwaysShowMask={false} />
          //                 )}
          //               </Field>
          //               <ErrorMessage className="error" component="div" name="phone" />
          //             </div>
          //           </div>
          //           {
          //             submitState === 'failed' &&
          //             <div className='error'>{errorMessage}</div>
          //           }
          //           <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">{buttonText}</button>
          //         </Form>
          //       )
          //     }
          //   }
          // </Formik>
        }
        {
          submitState === 'success' &&
          <div className="success">
            {props.blok.success_content && props.blok.success_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
          </div>
        }
        {
          submitState === 'ineligible' &&
          <div className="success">
            {props.blok.ineligible_content && props.blok.ineligible_content.map((blok) => React.createElement(Components(blok.component), { key: blok._uid, blok: blok }))}
          </div>
        }
      </div>
    </SbEditable>
  )
}

export default LeadFormConEd